// @flow
import * as React from 'react';
import { useState } from 'react';
import { ButtonBase, Skeleton, Stack, Tab as MuiTab, Tabs, Typography } from '@mui/material';
import { useClient } from '../../hooks/client-context';
import { useMedicareQuoteQuery } from '@coverright/data-access/medicare';
import { mainBackendApolloClient } from '@coverright/data-access/apollo-clients';
import { ApolloProvider } from '@apollo/client';
import { Doctors, DoctorsSkeleton } from './doctors';
import { LastCallSummary } from './last-call-summary';
import { ClientStatusDates } from './client-status-dates';
import { Drugs, DrugsSkeleton } from './drugs';
import { NotesOverview } from './notes-overview';
import { NextActionsOverview } from './next-actions-overview';
import { CalendlyAppointments } from './calendly-appointments';
import { ClientTags } from '../client-tags';
import { PropsWithClient } from '../../../../shared/api/use-client-subscription';
import { AiBadge, NextActionFormButton, AgentView, TabLabel } from '../../../../shared';
import { getUserName } from '@coverright/utils';
import { StatusProgress } from './status-row/status-progress';
import { ListChecks, MagicWand, Sparkle } from '@phosphor-icons/react';
import { keycloak } from '@coverright/shared/keycloak';
import { useClientNextActions } from '../../hooks/use-client-next-actions';
import { useStatusForm, withStatusForm } from '../../../../features';
import { ManagerNote } from './manager-note';
import moment from 'moment';


export const ClientOverview = () => {
  const client = useClient();
  const [tab, setTab] = useState(0);
  const { data, loading } = useMedicareQuoteQuery({
    variables: {
      id: client.quoteId
    },
    client: mainBackendApolloClient,
    skip: !client.quoteId
  });
  const nextActions = useClientNextActions(client.id)

  const quote = data?.medicareQuote;

  return (
    <Stack gap={3}>
      <Stack gap={2}>
        <Stack gap={2} direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
          <StatusProgress status={client.status} />
          <AgentButton client={client} />
        </Stack>
        <Stack gap={2} direction={'row'} alignItems={'flex-start'} justifyContent={'space-between'}>
          <Stack gap={1} direction={'row'} alignItems={'flex-start'}>
            <Typography variant={'body2'} color={'text.secondary'} mr={1}>Tags:</Typography>
            <Stack gap={.5}>
              <ClientTags clientId={client.id} tags={client.tags} />
              {!!client.scheduledTagPlacement && <Typography variant={"caption"} align={'left'} color={'text.secondary'}>Tag will be placed in the end of day.</Typography>}
            </Stack>
          </Stack>
          <ClientStatusDates status={client.status} followUpDate={client.followUpDate}
                             expectedCloseDate={client.expectedCloseDate}
                             disqualifiedReason={client.disqualifiedReason} />
        </Stack>
      </Stack>
      <ApolloProvider client={mainBackendApolloClient}>
        <Stack gap={3} direction={'row'}>
          {quote ? <Doctors quote={quote} /> : <DoctorsSkeleton />}
          {quote ? <Drugs quote={quote} /> : <DrugsSkeleton />}
        </Stack>
      </ApolloProvider>
      <Stack gap={3} direction={'row'}>
        <Stack flex={1} gap={2}>
          <Stack gap={2} direction={'row'} alignItems={'center'} >
            <Tabs value={tab} onChange={(e, value) => setTab(value)} sx={{flex: 1}}>
              <MuiTab label="Pre-Call Briefing" iconPosition={'start'}
                      icon={<Sparkle size={16} weight={'fill'} color={tab === 0 ? '#7C3AED' : 'currentColor'} />} />
              <MuiTab label={<TabLabel label={nextActions.filter(a => !a.done).length}>Next actions</TabLabel>}
                      iconPosition={'start'} icon={<ListChecks size={16} weight={'bold'} color="currentColor" />} />
              <MuiTab label="Last call summary" iconPosition={'start'} icon={<MagicWand size={16} weight={'bold'} color="currentColor" />} />
            </Tabs>
            <NextActionFormButton initialValues={{
              clientId: client.id,
              clientName: getUserName(client),
              assigneeAgentId: keycloak?.tokenParsed?.sub,
              done: false,
              title: ''
            }} />
          </Stack>

          {tab === 0 && <PreCallBriefing client={client} />}
          {tab === 1 && <NextActionsOverview />}
          {tab === 2 && <LastCallSummary calls={client.calls} />}
        </Stack>


        <NotesOverview notes={client.notes} />
      </Stack>

      <Stack gap={3} direction={'row'} alignItems={'flex-start'}>
        <CalendlyAppointments />
        <ManagerNote />
      </Stack>

    </Stack>
  );
};

const BRIEFING_SECTIONS = [
  { key: 'context',       label: 'Client Situation' },
  { key: 'talkingPoints', label: 'Talking Points' },
  { key: 'callSummaries', label: 'Recent Calls' },
  { key: 'notesSummary',  label: 'Notes Summary' },
  { key: 'emailsSummary', label: 'Emails Summary' },
] as const;

const PreCallBriefing = ({ client }: { client: any }) => {
  const briefing = client.preCallBriefing;

  if (briefing == null) {
    return (
      <Typography variant={'body2'} color={'text.secondary'}>
        Briefing will be generated after the first call is processed.
      </Typography>
    );
  }

  return (
    <Stack sx={{
      bgcolor: '#FAFAFA',
      borderLeft: '2px solid #7C3AED',
      borderRadius: '0 8px 8px 0',
      p: 2,
      gap: 2,
    }}>
      <Stack direction={'row'} alignItems={'center'} gap={1}>
        <Sparkle size={16} weight={'fill'} color={'#7C3AED'} />
        <Stack flex={1}>
          <Typography variant={'h5'}>Pre-Call Briefing</Typography>
          {briefing.updatedAt && (
            <Typography variant={'caption'} color={'text.secondary'}>
              Updated {moment(briefing.updatedAt).format('L LT')}
            </Typography>
          )}
        </Stack>
        <AiBadge />
      </Stack>

      {BRIEFING_SECTIONS.map(({ key, label }) => {
        const items: string[] | null | undefined = briefing[key];
        if (!items || items.length === 0) return null;
        return (
          <Stack key={key} gap={0.5}>
            <Typography variant={'caption'} sx={{
              fontWeight: 700,
              color: '#7C3AED',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}>
              {label}
            </Typography>
            {items.map((point: string, i: number) => (
              <Stack key={i} direction={'row'} gap={1} alignItems={'flex-start'}>
                <Typography sx={{ color: '#7C3AED', lineHeight: '22px' }}>•</Typography>
                <Typography variant={'body2'} color={'text.secondary'}>{point}</Typography>
              </Stack>
            ))}
          </Stack>
        );
      })}
    </Stack>
  );
};

const AgentButton = withStatusForm(({client}: PropsWithClient) => {
  const {open} = useStatusForm();

  if (!client) {
    return <Skeleton variant={'rounded'} width={125} height={48} />
  }

  return <ButtonBase sx={{borderRadius: '4px', bgcolor: '#f6fafb', border: '1px solid #dfeff3', p: '4px 8px'}}
                     onClick={() => open(client)}
                     id={'agent-button'} >
    <AgentView agent={client.agent ? {
      fullName: getUserName(client.agent),
      email: client.agent.email,
      picUrl: client.agent.picUrl
    } : { fullName: 'No agent', }} />
  </ButtonBase>
})
